import { creditCardFormConfig } from "checkout-query-client/configs/credit-card-form-config";
import { ecpFormConfig } from "checkout-query-client/configs/ecp-form-config";
import { giftCardFormConfig } from "checkout-query-client/configs/gift-card-form-config";
import { lpmFormConfig } from "checkout-query-client/configs/lpm-form-config";
import { payByBankFormConfig } from "checkout-query-client/configs/pay-by-bank-form-config";
import { pazeFormConfig } from "checkout-query-client/configs/paze-form-config";
import { savedCreditCardFormConfig } from "checkout-query-client/configs/saved-credit-card-form-config";
import { savedEcpFormConfig } from "checkout-query-client/configs/saved-ecp-form-config";
import { savedSepaFormConfig } from "checkout-query-client/configs/saved-sepa-form-config";
import { sepaFormConfig } from "checkout-query-client/configs/sepa-form-config";
import { CheckoutSession } from "checkout-query-client/hooks/use-checkout-session";
import { MerchantSettings } from "checkout-query-client/hooks/use-merchant-settings";
import {
  ChexForm,
  ChexFormConfig,
  ChexFormInputConfig,
} from "checkout-query-client/types/chex-form-fields";
import { PaymentType } from "checkout-query-client/types/form-types";
import {
  isAltPaymentType,
  isCreditCardPaymentMethod,
  isEcpPaymentMethod,
} from "checkout-query-client/utils/helpers";

import {
  CardPaymentMethod,
  EcpPaymentMethod,
  ExpirationDateFormat,
  PaymentGatewayType,
  PlainMessage,
  SepaPaymentMethod,
} from "frontend-client";

export const generatePaymentForm = (
  checkoutSession?: CheckoutSession,
  merchantSettings?: MerchantSettings,
  paymentType: PaymentType = PaymentType.creditCard,
  formId?: string
) => {
  // copy to prevent import global update
  const updatedEcpFormConfig = { ...ecpFormConfig };
  const merchantSetting =
    paymentType === PaymentType.applePay ||
    paymentType === PaymentType.googlePay
      ? undefined
      : paymentType === PaymentType.paze
        ? merchantSettings?.paymentMethods?.walletPaymentMethod?.paze
        : isAltPaymentType(paymentType)
          ? merchantSettings?.paymentMethods?.altPaymentMethod?.[paymentType]
          : merchantSettings?.paymentMethods?.[paymentType];

  const billingAddressCollection = (
    merchantSetting as
      | PlainMessage<CardPaymentMethod>
      | PlainMessage<EcpPaymentMethod>
  )?.billingAddressCollection;
  const billingAddressEnabled =
    isAltPaymentType(paymentType) || billingAddressCollection?.enabled;
  const showBillingSameAsShippingCheckbox =
    // isAltPaymentType(paymentType) ||
    billingAddressCollection?.showBillingSameAsShippingCheckbox;
  const addressCityState =
    // isAltPaymentType(paymentType) ||
    billingAddressCollection?.collectAddressCityState;
  const addressLine2 =
    // isAltPaymentType(paymentType) ||
    billingAddressCollection?.collectAddressLine2;
  const cardholderName =
    isAltPaymentType(paymentType) ||
    billingAddressCollection?.collectCardholderName;
  // TODO: uncomment this line once the decision is made for SEPA and eCheck. PO and Design teams should decide about full name in SEPA and eCheck, should it be included or not.
  //const accountHolderName = billingAddressCollection?.collectAccountHolderName;
  const nameOnCard = (merchantSetting as CardPaymentMethod)?.collectNameOnCard;
  const collectConsentCC = (merchantSetting as CardPaymentMethod)
    .collectConsentToStoreForFuturePayments;
  const collectConsentECP = (merchantSetting as EcpPaymentMethod)
    .collectConsentToStoreForFuturePayments;
  const collectConsentSEPA = (merchantSetting as SepaPaymentMethod)
    .collectConsentToStoreForFuturePayments;
  const expirationDateFormatSingleInput =
    (merchantSetting as CardPaymentMethod)?.expirationDateFormat ===
    ExpirationDateFormat.SINGLE_INPUT;

  const isSavedPaymentMethod = Boolean(paymentType !== formId);
  const { paymentMethods, saveConsumerProfile, billingAddress } =
    checkoutSession ?? {};
  const index = isSavedPaymentMethod
    ? paymentMethods?.findIndex((p) => p.paymentMethodId === formId)
    : undefined;
  const savedPaymentMethod =
    isSavedPaymentMethod && index !== undefined
      ? paymentMethods?.[index]
      : undefined;
  let paymentConfig: ChexFormConfig | undefined;

  switch (paymentType) {
    case PaymentType.creditCard:
      paymentConfig = isSavedPaymentMethod
        ? savedCreditCardFormConfig
        : creditCardFormConfig;
      break;
    case PaymentType.eCheck:
      paymentConfig = isSavedPaymentMethod
        ? savedEcpFormConfig
        : updatedEcpFormConfig;
      break;
    case PaymentType.applePay:
      paymentConfig = undefined;
      break;
    case PaymentType.sepa:
      paymentConfig = isSavedPaymentMethod
        ? savedSepaFormConfig
        : sepaFormConfig;
      break;
    case PaymentType.paze:
      paymentConfig = pazeFormConfig;
      break;
    case PaymentType.payByBank:
      paymentConfig = payByBankFormConfig;
      break;
    case PaymentType.ideal:
    case PaymentType.trustly:
    case PaymentType.paypal:
    case PaymentType.wechatPay:
    case PaymentType.alipay:
      paymentConfig = lpmFormConfig;
      break;
    case PaymentType.giftCard:
      paymentConfig = giftCardFormConfig;
      break;
  }
  let fields = {};
  if (merchantSetting?.enabled && paymentConfig) {
    fields = paymentConfig.inputs.reduce(
      (acc: ChexForm["fields"], formInput: ChexFormInputConfig) => {
        const { id } = formInput;
        let { active } = formInput;
        let initialValue = formInput.initialValue ?? "";
        if (
          !billingAddressEnabled &&
          [
            "billingTitle",
            "billingAddressCountry",
            "billingAddressZip",
            "billingAddressCity",
            "billingAddressAdministrativeArea",
            "doAddressesMatch",
            "billingAddressAddressLine1",
            "billingAddressAddressLine2",
          ].includes(id)
        ) {
          return acc;
        }

        if (
          !showBillingSameAsShippingCheckbox &&
          ["currentShippingAddress"].includes(id)
        ) {
          return acc;
        }

        if (
          !addressCityState &&
          [
            "billingAddressAddressLine1",
            "billingAddressAddressLine2",
            "billingAddressCity",
            "billingAddressAdministrativeArea",
          ].includes(id)
        ) {
          return acc;
        }

        if (!addressLine2 && id === "billingAddressAddressLine2") {
          return acc;
        }

        if (!showBillingSameAsShippingCheckbox && id === "doAddressesMatch") {
          return acc;
        }

        // TODO: uncomment this line once the decision is made for SEPA and eCheck. PO and Design teams should decide.
        if (
          !cardholderName &&
          // !accountHolderName &&
          id === "billingAddressName"
        ) {
          return acc;
        }

        if (
          !showBillingSameAsShippingCheckbox &&
          !savedPaymentMethod &&
          [
            "billingAddressAddressLine1",
            "billingAddressAddressLine2",
            "billingAddressZip",
            "billingAddressCity",
            "billingAddressAdministrativeArea",
            "billingAddressCountry",
            "billingAddressName",
          ].includes(id)
        ) {
          active = true;
        }

        if (
          billingAddressEnabled &&
          !showBillingSameAsShippingCheckbox &&
          billingAddress?.address
        ) {
          if (id === "billingAddressZip") {
            initialValue = billingAddress.address.postalCode;
          }
          if (id === "billingAddressName") {
            initialValue = billingAddress.address.recipients.join(" ");
          }
          if (id === "billingAddressAddressLine1") {
            initialValue = billingAddress.address.addressLines[0];
          }
          if (id === "billingAddressAddressLine2" && addressLine2) {
            initialValue = billingAddress.address.addressLines[1];
          }
          if (id === "billingAddressAdministrativeArea") {
            initialValue = billingAddress.address.administrativeArea;
          }
          if (id === "billingAddressCity") {
            initialValue = billingAddress.address.locality;
          }
          if (id === "billingAddressCountry") {
            initialValue = billingAddress.address.regionCode;
          }
        }

        if (
          id === "billingAddressCountry" &&
          merchantSettings?.country &&
          !billingAddress?.address?.regionCode
        ) {
          initialValue = merchantSettings?.country;
        }

        if (merchantSetting && isCreditCardPaymentMethod(merchantSetting)) {
          if (!nameOnCard && id === "nameOnCard") {
            return acc;
          }

          if (nameOnCard && id === "nameOnCard" && billingAddress?.address) {
            initialValue = billingAddress.address.recipients.join(" ");
          }

          // Evidently, if expiry date is not required we're supposed to hide the fields entirely
          if (!merchantSetting?.expiryDateRequired) {
            if (
              id === "expiration" ||
              id === "expirationMonth" ||
              id === "expirationYear"
            ) {
              return acc;
            }
          }
          if (expirationDateFormatSingleInput) {
            if (id === "expirationMonth" || id === "expirationYear") {
              return acc;
            }
            if (id === "expiration") {
              active = true;
            }
          } else {
            if (id === "expirationMonth" || id === "expirationYear") {
              active = true;
            }
            if (id === "expiration") {
              return acc;
            }
          }

          if (id === "securityCode") {
            if (!merchantSetting?.securityCodeRequired) {
              return acc;
            } else if (merchantSetting.captureSecurityCodeOnCardUpdate) {
              active = true;
            }
          }

          if (
            id === "expiration" &&
            savedPaymentMethod?.paymentMethod.case === "card"
          ) {
            initialValue = `${
              savedPaymentMethod.paymentMethod.value.expirationMonth
            } / ${
              savedPaymentMethod.paymentMethod.value.expirationYear - 2000
            }`.padStart(7, "0"); // 7 because ' / ' is 3 and month and year should be 2 each, prevents breaking when month is single digit
          }

          // Handle separate dropdowns for saved payment method
          if (
            !expirationDateFormatSingleInput &&
            savedPaymentMethod?.paymentMethod.case === "card"
          ) {
            if (id === "expirationMonth") {
              initialValue =
                savedPaymentMethod.paymentMethod.value.expirationMonth.toString();
            }

            if (id === "expirationYear") {
              initialValue =
                savedPaymentMethod.paymentMethod.value.expirationYear.toString();
            }
          }

          if (
            savedPaymentMethod?.paymentMethod.case === "card" &&
            id === "nameOnCard"
          ) {
            initialValue =
              savedPaymentMethod.paymentMethod.value.cardHolderName;
          }

          if (
            id === "collectConsentToStoreForFuturePayments" &&
            saveConsumerProfile !== undefined
          ) {
            // show consent checkbox when save consumer profile intent is TRUE and checkout collects consent
            active = saveConsumerProfile && collectConsentCC;
          }

          if (id === "setAsDefault" && saveConsumerProfile !== undefined) {
            // hide set as default checkbox when save consumer profile intent is FALSE;
            // comment this part for return user and setAsDefault checkbox.
            // if (!saveConsumerProfile) {
            //   return acc;
            // }
            // only show set as default checkbox when there are saved cards.
            // we also don't want to show setAsDefault for New payment methods if it's a save profile
            // we are checking it in form-section, so that in case of new payment method and Not-Saved profile
            // we can set this value to true in RHF form fields.
            active = saveConsumerProfile || collectConsentCC;
          }
        } else if (merchantSetting && isEcpPaymentMethod(merchantSetting)) {
          if (id === "confirmAccountNumber") {
            if (
              !(merchantSetting as PlainMessage<EcpPaymentMethod>)
                ?.confirmAccountNumber
            ) {
              return acc;
            }
          }
          if (id === "confirmRoutingNumber") {
            if (
              !(merchantSetting as PlainMessage<EcpPaymentMethod>)
                ?.confirmRoutingNumber
            ) {
              return acc;
            }
          }

          if (
            id === "collectConsentToStoreForFuturePayments" &&
            saveConsumerProfile !== undefined
          ) {
            // show consent checkbox when save consumer profile intent is TRUE and checkout collects consent
            active = saveConsumerProfile && collectConsentECP;
          }

          if (id === "setAsDefault") {
            // hide set as default checkbox when consumer profile intent is FALSE;
            if (!saveConsumerProfile) {
              return acc;
            }

            if (
              paymentType === PaymentType.eCheck &&
              merchantSettings?.supportedPaymentGatewayType ===
                PaymentGatewayType.UPG
            ) {
              active = false;
            }

            // only show set as default checkbox when there are saved echecks.
            active = saveConsumerProfile && collectConsentECP;
          }
          if (
            savedPaymentMethod?.paymentMethod.case === "electronicCheck" &&
            id === "nameOnAccount"
          ) {
            initialValue =
              savedPaymentMethod.paymentMethod.value.accountHolderName;
          }
        } else if (merchantSetting && paymentType === PaymentType.sepa) {
          if (
            id === "collectConsentToStoreForFuturePayments" &&
            saveConsumerProfile !== undefined
          ) {
            // show consent checkbox when save consumer profile intent is TRUE and checkout collects consent
            active = saveConsumerProfile && collectConsentSEPA;
          }

          if (id === "setAsDefault") {
            // hide set as default checkbox when consumer profile intent is FALSE;
            if (!saveConsumerProfile) {
              return acc;
            }

            if (
              merchantSettings?.supportedPaymentGatewayType ===
              PaymentGatewayType.UPG
            ) {
              active = false;
            }

            // only show set as default checkbox when there are saved SEPAs.
            active = saveConsumerProfile && collectConsentSEPA;
          }

          if (
            savedPaymentMethod?.paymentMethod.case === "sepa" &&
            id === "sepaNameOnAccount"
          ) {
            initialValue =
              savedPaymentMethod.paymentMethod.value.accountHolderName;
          }
        } else if (merchantSettings && paymentType === PaymentType.giftCard) {
          if (id === "pinCode")
            active =
              merchantSettings.paymentMethods?.giftCardPaymentMethod
                ?.collectPinCode ?? false;
        }

        acc[id] = {
          ...formInput,
          id,
          active,
          initialValue,
        };

        return acc;
      },
      {}
    );
  }
  return Promise.resolve({
    fields,
  } as ChexForm);
};
